import { ERC20 } from '../../abi/abi'
import Web3 from 'web3'
import { Token } from '../../logic/token/token'
import { Address, Wei } from '../../utils/types'
import { approveAmount } from '../../config'
import { maxApprove } from '../../utils/static-web3'

export class TokenApi {
  tokenContract

  constructor(public web3: Web3, public account: Address, public token: Token) {
    // @ts-ignore  ERC20 abi type is not totally correct, but it's working

    this.tokenContract = new web3.eth.Contract(ERC20, this.token.address)
  }

  isNative() {
    const nativePossibilities = ['MATIC']
    return nativePossibilities.includes(this.token.symbol)
  }

  async getNativeBalance(): Promise<Wei> {
    const result = await this.web3.eth.getBalance(this.account)
    return result || '0'
  }

  /**
   * ERC20 and Native are kind of unified
   * TODO: should be renamed getBalance()
   */
  async getERC20Balance(): Promise<Wei> {
    if (this.isNative()) {
      return this.getNativeBalance()
    }
    const result = await this.tokenContract.methods
      .balanceOf(this.account)
      .call({})
    return result || '0'
  }

  async isAllowed(other: Address, max: Wei = approveAmount) {
    if (this.isNative()) {
      return true
    }
    const allowanceWei = await this.tokenContract.methods
      .allowance(this.account, other)
      .call({ from: this.account })

    return Number(allowanceWei) >= Number(max)
  }

  async currentBalance(): Promise<Wei> {
    const balance = await this.tokenContract.methods
      .balanceOf(this.account)
      .call({ from: this.account })

    return balance
  }

  async hasEnough(amount: Wei) {
    const balance = this.currentBalance()

    return Number(balance) >= Number(amount)
  }

  async allowance(other: Address): Promise<Wei> {
    if (this.isNative()) {
      return maxApprove
    }
    const allowanceWei = await this.tokenContract.methods
      .allowance(this.account, other)
      .call({ from: this.account })

    return allowanceWei
  }

  async resetAllowance(other: Address) {
    // approve
    const approveGas = await this.tokenContract.methods
      .approve(other, '0')
      .estimateGas({ from: this.account })
    await this.tokenContract.methods.approve(other, '0').send({
      from: this.account,
      gas: approveGas,
      maxPriorityFeePerGas: null,
      maxFeePerGas: null,
    })
  }

  async allow(other: Address, max: Wei = approveAmount) {
    const isAllowed = await this.isAllowed(other, max)

    // approve
    if (!isAllowed) {
      const approveGas = await this.tokenContract.methods
        .approve(other, approveAmount)
        .estimateGas({ from: this.account })
      await this.tokenContract.methods.approve(other, approveAmount).send({
        from: this.account,
        gas: approveGas,
        maxPriorityFeePerGas: null,
        maxFeePerGas: null,
      })
    }
    return max
  }

  async transfer(other: Address, amount: Wei): Promise<boolean> {
    const [isAllowed, hasEnough] = await Promise.all([
      this.isAllowed(other, amount),
      this.hasEnough(amount),
    ])

    // approve
    if (!hasEnough || !isAllowed) {
      return false
    } else {
      const approveGas = await this.tokenContract.methods
        .transfer(other, amount)
        .estimateGas({ from: this.account })
      await this.tokenContract.methods.transfer(other, amount).send({
        from: this.account,
        gas: approveGas,
        maxPriorityFeePerGas: null,
        maxFeePerGas: null,
      })
    }

    return true
  }

  static async getTokenInfo(web3: Web3, tokenAddress: Address): Promise<Token> {
    // @ts-ignore
    const tokenContract = new web3.eth.Contract(ERC20, tokenAddress)

    const [decimals, name, symbol] = await Promise.all([
      tokenContract.methods.symbol().call(),
      tokenContract.methods.decimals().call(),
      tokenContract.methods.name().call(),
    ])
    return {
      address: tokenAddress,
      name,
      symbol,
      decimals: tokenContract.methods.decimals(),
    }
  }
}
